import * as z from 'zod';

/** Suffixes i18next appends to a base key when `count` is supplied. */
const pluralSuffixes = ['zero', 'one', 'two', 'few', 'many', 'other'] as const;

type PluralSuffix = (typeof pluralSuffixes)[number];

type BaseKey<Key extends string> = Key extends `${infer Base}_${PluralSuffix}` ? Base : Key;

/**
 * Dot-separated paths to every string leaf of a catalog, with plural suffixes
 * collapsed to the base key callers actually pass to `t()`.
 *
 * Deriving key unions from the JSON means a new entry is usable the moment it is
 * written, with no parallel list to maintain.
 */
export type TranslationKeyPath<Translations> = {
  [Key in keyof Translations & string]: Translations[Key] extends string
    ? BaseKey<Key>
    : `${Key}.${TranslationKeyPath<Translations[Key]>}`;
}[keyof Translations & string];

/** A catalog is a tree of strings; JSON imports structurally match this. */
export type TranslationNode = string | { [key: string]: TranslationNode };

const leafSchema = z.string();

const translationNodeSchema: z.ZodType<TranslationNode> = z.lazy(() =>
  z.union([leafSchema, z.record(z.string(), translationNodeSchema)]),
);

const branchSchema = z.record(z.string(), translationNodeSchema);

/** Type guard: a translation leaf is a string. */
export const isTranslationLeaf = (node: TranslationNode): node is string =>
  leafSchema.safeParse(node).success;

/** Type guard: a translation branch is a record of translation nodes. */
export const isTranslationBranch = (
  node: TranslationNode,
): node is Record<string, TranslationNode> => branchSchema.safeParse(node).success;

const stripPluralSuffix = (key: string): string => {
  for (const suffix of pluralSuffixes) {
    const ending = `_${suffix}`;
    if (key.endsWith(ending)) {
      return key.slice(0, -ending.length);
    }
  }

  return key;
};

/** Runtime counterpart of {@link TranslationKeyPath}. */
export const collectTranslationKeyPaths = (translations: TranslationNode): string[] => {
  const branch = branchSchema.safeParse(translations);
  if (!branch.success) {
    return [];
  }

  const paths: string[] = [];

  for (const [key, value] of Object.entries(branch.data)) {
    if (leafSchema.safeParse(value).success) {
      paths.push(stripPluralSuffix(key));
      continue;
    }

    for (const nestedPath of collectTranslationKeyPaths(value)) {
      paths.push(`${key}.${nestedPath}`);
    }
  }

  return paths;
};

/**
 * Reads one string out of a catalog by dot path, resolving the plural variant
 * when a count is supplied. Used where no i18next instance is available.
 */
export const readTranslationString = (
  translations: TranslationNode,
  path: string,
  count?: number,
): string | null => {
  const pluralPath = count === undefined ? null : `${path}_${count === 1 ? 'one' : 'other'}`;
  const candidates = pluralPath === null ? [path] : [pluralPath, path];

  for (const candidate of candidates) {
    let current: TranslationNode | undefined = translations;
    let resolved = true;

    for (const segment of candidate.split('.')) {
      const branch = branchSchema.safeParse(current);
      if (!branch.success) {
        resolved = false;
        break;
      }
      current = branch.data[segment];
    }

    const leaf = resolved ? leafSchema.safeParse(current) : null;
    if (leaf?.success) {
      return leaf.data;
    }
  }

  return null;
};
